/**
 * Attorney Portal Layout
 * Matches the main app layout with blue sidebar
 * Context-aware: Analysis tools only show when viewing a case
 * Sidebar uses scroll-to-section navigation with IntersectionObserver highlighting
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Home,
  FileText,
  ScrollText,
  Compass,
  ShieldCheck,
  FileSearch,
  FolderOpen,
  Scale,
  Download,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  User,
  Building2,
  BarChart3,
  Target,
  Shield,
  Clock,
  TrendingUp,
  History,
  Lightbulb,
  Wrench,
  Gavel,
  AlertTriangle,
  Search,
  BookOpen,
  Settings,
  Dna,
  Brain,
  ClipboardList,
  Calendar
} from 'lucide-react';
import { useAuth, useUser, useOrganization, useClerk } from '@clerk/clerk-react';
import { getAuthHeaders } from '../lib/authHeaders';
import { isCompanyStaffSession } from '../contexts/AttorneyAuthContext';
import { BRAND_NAME } from '../config/brand';
import { FigureCoWordmark, FigureCoMonogram } from './brand/FigureCoLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface AttorneyLayoutProps {
  children: React.ReactNode;
  activeSection?: string;
  onSectionChange?: (section: string) => void;
}

interface NavItem {
  icon: React.ElementType;
  label: string;
  path?: string;
  section?: string;
  badge?: string | number;
}

export default function AttorneyLayout({ children, activeSection, onSectionChange }: AttorneyLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // Clerk-only cutover (2026-07-22): attorneys authenticate via their Clerk
  // session with an active firm Organization — same convention as
  // ProtectedAttorneyRoute in AttorneyAuthContext.tsx (orgId present ⇒
  // attorney). The legacy attorney_token/attorney_profile context is now
  // inert, so this layout no longer reads it.
  const { isLoaded: clerkLoaded, isSignedIn, orgId } = useAuth();
  // useUser() loads the User object (email + metadata) on its own clock,
  // lagging useAuth(). isCompanyStaffSession(user) needs it, so gate on
  // userLoaded too — else a null user bounces staff before it hydrates.
  const { isLoaded: userLoaded, user } = useUser();
  const { organization } = useOrganization();
  const { signOut } = useClerk();

  // Display fields derived from Clerk, replacing the old localStorage-backed
  // AttorneyProfile (name/email/law_firm_name).
  const attorneyName = user?.fullName || user?.firstName || user?.primaryEmailAddress?.emailAddress || 'Attorney';
  const attorneyEmail = user?.primaryEmailAddress?.emailAddress || '';
  const firmName = organization?.name || '';

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [casesCount, setCasesCount] = useState(0);
  const [visibleSection, setVisibleSection] = useState<string>('');

  // Determine if we're viewing a case detail (exact case detail page, not sub-pages)
  const isViewingCase = location.pathname.includes('/attorney/case/');
  const isOnCaseDetailPage = isViewingCase && !location.pathname.includes('/settlement') && !location.pathname.includes('/tools') && !location.pathname.includes('/demand-letter') && !location.pathname.includes('/timeline');
  const currentCaseId = isViewingCase
    ? location.pathname.split('/attorney/case/')[1]?.split('/')[0]
    : null;

  // IntersectionObserver for scroll-based section highlighting
  useEffect(() => {
    if (!isOnCaseDetailPage) return;

    const sectionIds = [
      'overview', 'pro-forma', 'attorney-analysis', 'adjuster-analysis',
      'fair-settlement', 'monte-carlo', 'timeline', 'sensitivity',
      'historical', 'what-if', 'strategy-builder', 'litigation-econ',
      'game-theory', 'bad-faith', 'case-law', 'evidence', 'demand-letter',
      'proof-of-loss', 'euo-prep-memo', 'ror-response', 'ror-analysis'
    ];

    const observer = new IntersectionObserver(
      (entries) => {
        // Find the most visible section
        const visible = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visible.length > 0) {
          setVisibleSection(visible[0].target.id);
        }
      },
      {
        rootMargin: '-100px 0px -40% 0px',
        threshold: [0, 0.25, 0.5]
      }
    );

    // Observe all section elements
    sectionIds.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [isOnCaseDetailPage, location.pathname]);

  useEffect(() => {
    // Check auth via Clerk. Wait for Clerk to load before deciding —
    // otherwise a freshly-mounted page would bounce a signed-in attorney to
    // /login on the first render before their session is known.
    if (!clerkLoaded || !userLoaded) return;

    // Admit firm attorneys (active org) OR company staff (cross-firm oversight).
    if (!isSignedIn || !(orgId || isCompanyStaffSession(user))) {
      // /attorney/login redirects to the app's Clerk sign-in (see
      // AttorneyLoginRedirect in App.tsx).
      navigate('/attorney/login');
      return;
    }

    // Fetch cases count
    fetchCasesCount();
  }, [clerkLoaded, userLoaded, isSignedIn, orgId, user]);

  const fetchCasesCount = async () => {
    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(`${BACKEND_URL}/api/v1/attorney/dashboard`, {
        headers: authHeaders
      });
      if (response.ok) {
        const data = await response.json();
        setCasesCount(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching cases count:', error);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/attorney/login');
  };

  // Scroll to a section by ID
  const scrollToSection = useCallback((sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Navigation items - Main (always visible)
  const mainNavItems: NavItem[] = [
    { icon: Home, label: 'Dashboard', path: '/attorney/dashboard', badge: casesCount || undefined },
    { icon: BarChart3, label: 'Portfolio', path: '/attorney/portfolio' },
  ];

  // Case Summary section - scroll-to-section items
  const caseSummaryNavItems: NavItem[] = [
    { icon: FileText, label: 'Overview', section: 'overview' },
    { icon: BarChart3, label: 'Pro Forma', section: 'pro-forma' },
  ];

  // Analysis section - scroll-to-section items
  const analysisNavItems: NavItem[] = [
    { icon: Scale, label: 'Attorney Analysis', section: 'attorney-analysis' },
    { icon: Shield, label: 'Adjuster Analysis', section: 'adjuster-analysis' },
    { icon: Target, label: 'Fair Settlement', section: 'fair-settlement' },
    { icon: BarChart3, label: 'Monte Carlo', section: 'monte-carlo' },
    { icon: Calendar, label: 'Timeline', section: 'timeline' },
  ];

  // Settlement Tools section - scroll-to-section items
  const settlementToolsNavItems: NavItem[] = [
    { icon: TrendingUp, label: 'Sensitivity', section: 'sensitivity' },
    { icon: History, label: 'Historical', section: 'historical' },
    { icon: Lightbulb, label: 'What-If', section: 'what-if' },
    { icon: Wrench, label: 'Strategy Builder', section: 'strategy-builder' },
    { icon: Scale, label: 'Litigation Econ', section: 'litigation-econ' },
    { icon: Brain, label: 'Game Theory', section: 'game-theory' },
  ];

  // Advanced section - scroll-to-section items
  const advancedNavItems: NavItem[] = [
    { icon: AlertTriangle, label: 'Bad Faith', section: 'bad-faith' },
    { icon: Search, label: 'Case Law', section: 'case-law' },
    { icon: ClipboardList, label: 'Evidence', section: 'evidence' },
    { icon: FileText, label: 'Demand Letter', section: 'demand-letter' },
    { icon: ScrollText, label: 'Proof of Loss', section: 'proof-of-loss' },
    { icon: Compass, label: 'EUO Prep Memo', section: 'euo-prep-memo' },
    { icon: ShieldCheck, label: 'RoR Response', section: 'ror-response' },
    { icon: FileSearch, label: 'RoR Analysis', section: 'ror-analysis' },
  ];

  const documentCenterNavItem: NavItem = {
    icon: FolderOpen,
    label: 'Document Center',
    path: currentCaseId ? `/attorney/case/${currentCaseId}/documents` : undefined,
  };

  // Resources - only visible when viewing a case
  const resourceNavItems: NavItem[] = [
    { icon: Download, label: 'Download PDF', section: 'download-pdf' },
    { icon: FileText, label: 'Download Word', section: 'download-docx' },
  ];

  const handleNavClick = (item: NavItem) => {
    if (item.path) {
      navigate(item.path);
    } else if (item.section) {
      // Handle download actions
      if (item.section === 'download-pdf' || item.section === 'download-docx') {
        if (onSectionChange) {
          onSectionChange(item.section);
        }
        setMobileMenuOpen(false);
        return;
      }

      // Navigate via hash regardless of whether we're already on the case
      // detail page. AttorneyCaseDetail's LEGACY_HASH_MAP effect (keyed on
      // location.hash) is what actually switches the lifecycle tab
      // (intake/analysis/deliverables/settlement) before scrolling to the
      // anchor. Sections only exist in the DOM once their tab is active, so
      // calling scrollToSection() directly here (same-page nav's previous
      // behavior) silently no-op'd for any section outside the current tab
      // — this reuses the same hash mechanism cross-page nav already relies
      // on instead of duplicating the tab-mapping logic.
      if (currentCaseId) {
        navigate(`/attorney/case/${currentCaseId}#${item.section}`);
      } else if (isOnCaseDetailPage) {
        // No case id resolved (shouldn't normally happen) — best-effort
        // direct scroll as a fallback.
        scrollToSection(item.section);
      }
      if (onSectionChange) {
        onSectionChange(item.section);
      }
    }
    setMobileMenuOpen(false);
  };

  // Determine the effective active section (from IntersectionObserver or prop)
  const effectiveActiveSection = visibleSection || activeSection || '';

  const NavItemButton = ({ item }: { item: NavItem }) => {
    const isActive = item.path
      ? location.pathname === item.path || (item.path !== '/attorney/dashboard' && location.pathname.startsWith(item.path))
      : (isOnCaseDetailPage && item.section === effectiveActiveSection);

    return (
      <button
        onClick={() => handleNavClick(item)}
        className={`
          w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm
          ${isActive
            ? 'bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)]'
            : 'hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)]'
          }
        `}
        style={isActive ? undefined : { color: 'var(--color-text-muted)' }}
        title={sidebarCollapsed ? item.label : undefined}
      >
        <item.icon className="w-4 h-4 flex-shrink-0" />
        {!sidebarCollapsed && <span className="font-medium">{item.label}</span>}

        {!sidebarCollapsed && item.badge !== undefined && (
          <span className="ml-auto text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}>
            {item.badge}
          </span>
        )}
      </button>
    );
  };

  const Sidebar = ({ mobile = false }: { mobile?: boolean }) => (
    <div className={`
      ${mobile ? 'w-64' : sidebarCollapsed ? 'w-16' : 'w-60'}
      bg-[var(--color-bg-base)] text-[var(--color-text-primary)] transition-all duration-300 flex flex-col h-full
    `}>
      {/* Logo */}
      <div className="p-4 border-b border-[var(--color-hairline)]">
        <div 
          className="flex items-center justify-center cursor-pointer"
          onClick={() => navigate('/attorney/dashboard')}
          title="Go to Dashboard"
        >
          {/* The Figure Co. logo (code, theme-aware). Monogram when collapsed. */}
          {sidebarCollapsed && !mobile ? (
            <span className="flex items-center justify-center py-1 text-text-primary hover:opacity-80 transition-opacity">
              <FigureCoMonogram size={32} />
            </span>
          ) : (
            <div className="flex items-center justify-center py-2 hover:opacity-80 transition-opacity">
              <FigureCoWordmark size="md" />
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-2">
        {/* Main Section - Always visible */}
        {(!sidebarCollapsed || mobile) && (
          <p className="px-3 py-2 text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">
            Main
          </p>
        )}
        <div className="space-y-0.5">
          {mainNavItems.map((item) => (
            <NavItemButton key={item.label} item={item} />
          ))}
        </div>

        {/* Case Summary Section - Only when viewing a case */}
        {isViewingCase && (
          <>
            {(!sidebarCollapsed || mobile) && (
              <p className="px-3 py-2 mt-4 text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">
                Case Summary
              </p>
            )}
            {sidebarCollapsed && !mobile && <div className="h-3" />}
            <div className="space-y-0.5">
              {caseSummaryNavItems.map((item) => (
                <NavItemButton key={item.label} item={item} />
              ))}
            </div>
          </>
        )}

        {/* Analysis Section - Only when viewing a case */}
        {isViewingCase && (
          <>
            {(!sidebarCollapsed || mobile) && (
              <p className="px-3 py-2 mt-4 text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">
                Analysis
              </p>
            )}
            {sidebarCollapsed && !mobile && <div className="h-3" />}
            <div className="space-y-0.5">
              {analysisNavItems.map((item) => (
                <NavItemButton key={item.label} item={item} />
              ))}
            </div>
          </>
        )}

        {/* Settlement Tools Section - Only when viewing a case */}
        {isViewingCase && (
          <>
            {(!sidebarCollapsed || mobile) && (
              <p className="px-3 py-2 mt-4 text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">
                Settlement Tools
              </p>
            )}
            {sidebarCollapsed && !mobile && <div className="h-3" />}
            <div className="space-y-0.5">
              {settlementToolsNavItems.map((item) => (
                <NavItemButton key={item.label} item={item} />
              ))}
            </div>
          </>
        )}

        {/* Document Center - Only when viewing a case */}
        {isViewingCase && documentCenterNavItem.path && (
          <div className="space-y-0.5 mt-2">
            <NavItemButton item={documentCenterNavItem} />
          </div>
        )}

        {/* Advanced Section - Only when viewing a case */}
        {isViewingCase && (
          <>
            {(!sidebarCollapsed || mobile) && (
              <p className="px-3 py-2 mt-4 text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">
                Advanced
              </p>
            )}
            {sidebarCollapsed && !mobile && <div className="h-3" />}
            <div className="space-y-0.5">
              {advancedNavItems.map((item) => (
                <NavItemButton key={item.label} item={item} />
              ))}
            </div>
          </>
        )}

        {/* Downloads Section - Only when viewing a case */}
        {isViewingCase && (
          <>
            {(!sidebarCollapsed || mobile) && (
              <p className="px-3 py-2 mt-4 text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">
                Downloads
              </p>
            )}
            {sidebarCollapsed && !mobile && <div className="h-3" />}
            <div className="space-y-0.5">
              {resourceNavItems.map((item) => (
                <NavItemButton key={item.label} item={item} />
              ))}
            </div>
          </>
        )}
      </nav>

      {/* User Section */}
      {(!sidebarCollapsed || mobile) && isSignedIn && (
        <div className="p-3 border-t border-[var(--color-hairline)]">
          <div className="flex items-center gap-3 p-2 rounded-lg">
            <div className="w-9 h-9 bg-[var(--color-brand)] rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5" style={{ color: 'var(--color-on-brand)' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>{attorneyName}</p>
              <p className="text-xs text-[var(--color-text-muted)] truncate">{firmName || 'Attorney Portal'}</p>
            </div>
          </div>

          {/*
            Weekly Digest button removed (2026-07-22 Clerk-only cutover): it
            opened <DigestSettings attorneyId={profile.id}>, which calls
            /api/v1/bi-timeline/digest/preferences/{attorneyId} — a real
            backend attorney-record id (weekly_digest_service.py resolves it
            via {"$or": [{"id": attorney_id}, {"_id": attorney_id}]} against
            the attorneys collection to find the attorney's email). Clerk's
            session only exposes a Clerk user id / org id, neither of which
            matches that collection, so wiring either in would silently 404
            every digest fetch/send. Needs a backend lookup (Clerk org ->
            attorney record id) before this can be re-wired correctly —
            flagged for conductor/backend follow-up, not guessed here.
          */}

          <button
            onClick={handleLogout}
            className="w-full mt-2 flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)] transition-colors"
            style={{ color: 'var(--color-text-muted)' }}
          >
            <LogOut className="w-5 h-5" />
            <span>Sign Out</span>
          </button>
        </div>
      )}

      {/* Collapse button - desktop only */}
      {!mobile && (
        <div className="p-3 border-t border-[var(--color-hairline)]">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center p-2 rounded-lg hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)] transition-colors"
            style={{ color: 'var(--color-text-muted)' }}
          >
            {sidebarCollapsed ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <>
                <ChevronLeft className="w-5 h-5" />
                <span className="ml-2 text-sm">Collapse</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--color-bg-base)' }}>
      {/* Desktop Sidebar */}
      <div className="hidden lg:block h-screen sticky top-0">
        <Sidebar />
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        >
          <div className="h-full" onClick={(e) => e.stopPropagation()}>
            <Sidebar mobile />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header */}
        <header className="sticky top-0 z-30" style={{ backgroundColor: 'var(--color-bg-surface)', borderBottom: '1px solid var(--color-border)' }}>
          <div className="flex items-center justify-between px-4 lg:px-6 h-16">
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 hover:bg-[var(--color-bg-hover)] rounded-lg"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            {/* Page title - mobile */}
            <div className="lg:hidden flex items-center gap-2">
              <Scale className="w-5 h-5 text-[var(--color-brand)]" />
              <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Attorney Portal</span>
            </div>

            {/* Breadcrumb / Title - desktop */}
            <div className="hidden lg:flex items-center gap-2">
              <Building2 className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
              <span style={{ color: 'var(--color-text-secondary)' }}>{firmName || 'Attorney Portal'}</span>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-3">
              {isSignedIn && (
                <div className="hidden sm:block text-right">
                  <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{attorneyName}</p>
                  <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{attorneyEmail}</p>
                </div>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--color-bg-hover)] rounded-lg transition-colors"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Sign Out</span>
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6">
          {children}
        </main>

        {/* Footer */}
        <footer className="px-4 lg:px-6 py-3" style={{ borderTop: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-surface)' }}>
          <p className="text-xs text-center" style={{ color: 'var(--color-text-muted)' }}>
            © 2026 {BRAND_NAME}. Attorney Portal — All data is confidential.
          </p>
        </footer>
      </div>
    </div>
  );
}
